import type { POICategory, POIType } from "../types/poi.types";

// Mapping categoria -> token tema (daisyUI v5) + fallback
export const CATEGORY_COLORS = {
    // 1. INFO & CULTURA (Blu/Viola - Orientamento)
    info: { cssVar: '--color-primary', fallback: '#3B82F6' },

    // 2. SICUREZZA (Rosso - Attenzione massima)
    safety: { cssVar: '--color-error', fallback: '#EF4444' },

    // 3. SERVIZI & RISTORO (Giallo/Arancio - Comfort)
    services: { cssVar: '--color-warning', fallback: '#F59E0B' },

    // 4. NATURA & AMBIENTE (Verde - Punti naturali)
    nature: { cssVar: '--color-success', fallback: '#10B981' },

    // 5. SPORT & AVVENTURA (Azzurro/Ciano - Azione)
    activity: { cssVar: '--color-info', fallback: '#06B6D4' },

    // 6. CAMPEGGIO (Viola/Fucsia - Ospitalità)
    camping: { cssVar: '--color-secondary', fallback: '#8B5CF6' },

    // 7. MOBILITÀ & REGOLE (Grigio/Slate - Logistica)
    mobility: { cssVar: '--color-neutral', fallback: '#64748B' },
} as const;

export type POICategoryId = keyof typeof CATEGORY_COLORS;


export const POI_CATEGORIES = [
    {
        id: 'info',
        label: 'Informazioni & Accessi',
        icon: '✨',
        symbol: 'stars', // Icona per la categoria generale
        types: [
            { id: 'info', label: 'Info (e. reception, informazioni generali ecc ecc)', icon: 'ℹ️', symbol: 'info' },
            { id: 'ticket', label: 'Biglietteria (es. area vendita biglietti)', icon: '🎟️', symbol: 'confirmation_number' },
            { id: 'meeting_point', label: 'Punto di ritrovo (es. luogo di incontro per gruppi o eventi ricorrenti)', icon: '📍', symbol: 'location_on' },
        ]
    },
    {
        id: 'safety',
        label: 'Sicurezza & Emergenze',
        icon: '🚨',
        symbol: 'warning',
        types: [
            { id: 'first_aid', label: 'Primo Soccorso (puoi usarla per indicare un punto di primo soccorso)', icon: '⛑️', symbol: 'medical_services' },
            { id: 'danger', label: 'Pericolo (puoi usarla per indicare un punto di pericolo temporaneo)', icon: '⚠️', symbol: 'warning' },
        ]
    },
    {
        id: 'services',
        label: 'Servizi di Base',
        icon: '🚻',
        symbol: 'home_repair_service',
        types: [
            { id: 'restroom', label: 'Bagni', icon: '🚻', symbol: 'wc' },
            { id: 'water', label: 'Acqua (fontana)', icon: '🚰', symbol: 'water_drops' },
            { id: 'shower', label: 'Doccia', icon: '🚿', symbol: 'shower' },
            { id: 'waste', label: 'Rifiuti', icon: '🚮', symbol: 'delete' },
        ]
    },
    {
        id: 'food',
        label: 'Ristoro',
        icon: '🍽️',
        symbol: 'restaurant',
        types: [
            { id: 'restaurant', label: 'Ristorante', icon: '🍽️', symbol: 'restaurant' },
            { id: 'bar', label: 'Bar', icon: '☕', symbol: 'local_cafe' },
            { id: 'vending', label: 'Distributore', icon: '🤖', symbol: 'shopping_basket' },
        ]
    },
    {
        id: 'camping',
        label: 'Campeggio',
        icon: '🏕️',
        symbol: 'campfire',
        types: [
            { id: 'camping', label: 'Area Camping', icon: '🏕️', symbol: 'camping' },
            { id: 'pitch', label: 'Piazzole (tenda, camper ecc ecc)', icon: '⛺', symbol: 'rv_hookup' },
        ]
    },
    {
        id: 'activity',
        label: 'Giochi & Avventura',
        icon: '🛝',
        symbol: 'recreation',
        types: [
            { id: 'playground', label: 'Parco Giochi', icon: '🛝', symbol: 'toys_fan' },
            { id: 'swimming_pool', label: 'Piscina', icon: '🏊', symbol: 'pool' },
            { id: 'adventure', label: 'Area Avventura', icon: '🌲', symbol: 'forest' },
            { id: 'sport_field', label: 'Campi da sport', icon: '⚽', symbol: 'sports_soccer' },
        ]
    },
    {
        id: 'culture',
        label: 'Cultura & Archeologia',
        icon: '🏛️',
        symbol: 'account_balance',
        types: [
            { id: 'monument', label: 'Monumento', icon: '🗿', symbol: 'museum' },
            { id: 'viewpoint', label: 'Punto Panoramico', icon: '🔭', symbol: '360' },
        ]
    },
    {
        id: 'nature',
        label: 'Natura & Percorsi',
        icon: '🥾',
        symbol: 'terrain',
        types: [
            { id: 'trail', label: 'Sentiero', icon: '🥾', symbol: 'footprint' },
            { id: 'beach', label: 'Spiaggia', icon: '🏖️', symbol: 'beach_access' },
            { id: 'water_point', label: 'Lago, Fiume, Mare, Cascate', icon: '🏞️', symbol: 'water' },
            { id: 'nature_point', label: 'Natura, osservazione animali', icon: '🦌', symbol: 'cruelty_free' },
        ]
    },
    {
        id: 'mobility',
        label: 'Mobilità',
        icon: '🅿️',
        symbol: 'directions_bus',
        types: [
            { id: 'parking', label: 'Parcheggio', icon: '🅿️', symbol: 'local_parking' },
            { id: 'shuttle', label: 'Navetta', icon: '🚌', symbol: 'departure_board' },
        ]
    },
    {
        id: 'rules',
        label: 'Regole & Accessibilità',
        icon: '♿',
        symbol: 'accessibility_new',
        types: [
            { id: 'quiet_area', label: 'Area Silenzio', icon: '🤫', symbol: 'volume_off' },
            { id: 'smoking_area', label: 'Area Fumatori', icon: '🚬', symbol: 'smoking_rooms' },
        ]
    }
] as const satisfies readonly POICategory<POIType>[];

/**
 * Assert compile-time: i POIType (derivati da POIMetaMap) DEVONO coincidere con i type dichiarati in POI_CATEGORIES.
 *
 * Così evitiamo drift:
 * - aggiungi un type in UI ma ti dimentichi di tipizzarlo (POIMetaMap)
 * - tipizzi un type ma non lo esponi nelle categorie
 */
type Assert<T extends true> = T;
type IsExact<A, B> =
    [Exclude<A, B>] extends [never]
    ? ([Exclude<B, A>] extends [never] ? true : false)
    : false;

type POITypeFromCategories = (typeof POI_CATEGORIES)[number]['types'][number]['id'];

type _assertPOITypeRegistryMatchesCategories = Assert<IsExact<POITypeFromCategories, POIType>>;
